import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Opt { id: string; label: string }

/**
 * Single-select lookup dropdown with a BOUNDED, scrollable, type-to-filter popup
 * (LIMS XAF combo parity) — the native <select> popup expands to fit every option
 * (40+ roles), which looks oversized. The popup is portaled to <body> behind a
 * dismiss backdrop and positioned from the trigger's rect, so it never clips
 * inside the dialog. Empty value = the placeholder ("N/A").
 */
export function LookupSelect({ value, options, onChange, placeholder = 'N/A' }: {
  value: string | null;
  options: Opt[];
  onChange: (v: string | null) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 200 });
  const btnRef = useRef<HTMLButtonElement>(null);

  const toggle = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 2, left: r.left, width: r.width });
    setQ('');
    setOpen((o) => !o);
  };
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const cur = options.find((o) => o.id === value);
  const list = q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;
  const pick = (id: string | null) => { onChange(id); setOpen(false); };

  return (
    <>
      <button type="button" ref={btnRef} className="acc-input acc-lksel-btn" onClick={toggle}>
        <span className={cur ? '' : 'acc-lksel-ph'}>{cur ? cur.label : placeholder}</span>
        <i className="ti ti-chevron-down" />
      </button>
      {open && createPortal(
        <>
          <div className="acc-lksel-backdrop" onMouseDown={() => setOpen(false)} />
          <div className="acc-lksel-pop" style={{ top: pos.top, left: pos.left, width: pos.width }}>
            <div className="acc-lksel-search">
              <input autoFocus placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="acc-lksel-list">
              <div className={`acc-lksel-opt${!value ? ' sel' : ''}`} onMouseDown={() => pick(null)}>{placeholder}</div>
              {list.map((o) => (
                <div key={o.id} className={`acc-lksel-opt${o.id === value ? ' sel' : ''}`} onMouseDown={() => pick(o.id)}>{o.label}</div>
              ))}
              {list.length === 0 && <div className="acc-lksel-empty">No matches</div>}
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
